(function() {
  'use strict';

  // =============================================================================
  // CONFIGURATION
  // =============================================================================

  var CONFIG = {
    // URL parameter name
    paramName: 'ref',

    // Storage keys
    localStorageKey: 'pawmate_referral_code',
    localStorageTimestampKey: 'pawmate_referral_timestamp',
    cookieName: 'pawmate_ref',

    // Expiry in days
    expiryDays: 30,

    // Referral code validation pattern (PAWMATE-XXXXX)
    codePattern: /^PAWMATE-[A-Z0-9]{5}$/i,

    // Cart attribute name for Shopify
    cartAttributeName: 'referral_code',

    // Debug mode (set to false in production)
    debug: false
  };

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  /**
   * Sanitize input to prevent XSS
   * @param {string} str - Input string
   * @returns {string} - Sanitized string
   */
  function sanitize(str) {
    if (typeof str !== 'string') return '';
    return str.replace(/[<>"'&]/g, function(char) {
      var entities = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
        '&': '&amp;'
      };
      return entities[char] || char;
    }).substring(0, 50); // Limit length
  }

  /**
   * Validate referral code format
   * @param {string} code - Referral code to validate
   * @returns {boolean} - Whether code is valid
   */
  function isValidCode(code) {
    if (!code || typeof code !== 'string') return false;
    return CONFIG.codePattern.test(code.toUpperCase());
  }

  /**
   * Log debug messages
   * @param {string} message - Message to log
   * @param {*} data - Optional data to log
   */
  function log(message, data) {
    if (CONFIG.debug) {
      console.log('[Pawmate Referral]', message, data !== undefined ? data : '');
    }
  }

  /**
   * Get current timestamp in milliseconds
   * @returns {number} - Current timestamp
   */
  function now() {
    return Date.now();
  }

  /**
   * Check if timestamp is expired
   * @param {number} timestamp - Timestamp to check
   * @returns {boolean} - Whether timestamp is expired
   */
  function isExpired(timestamp) {
    if (!timestamp) return true;
    var expiryMs = CONFIG.expiryDays * 24 * 60 * 60 * 1000;
    return (now() - timestamp) > expiryMs;
  }

  // =============================================================================
  // STORAGE FUNCTIONS
  // =============================================================================

  /**
   * Set cookie with expiry
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiry
   */
  function setCookie(name, value, days) {
    try {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      var expires = 'expires=' + date.toUTCString();
      document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/;SameSite=Lax;Secure';
      log('Cookie set:', name + '=' + value);
    } catch (e) {
      log('Cookie set failed:', e.message);
    }
  }

  /**
   * Get cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null
   */
  function getCookie(name) {
    try {
      var nameEQ = name + '=';
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
    } catch (e) {
      log('Cookie get failed:', e.message);
    }
    return null;
  }

  /**
   * Delete cookie
   * @param {string} name - Cookie name
   */
  function deleteCookie(name) {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax';
    log('Cookie deleted:', name);
  }

  /**
   * Set localStorage value
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   */
  function setLocalStorage(key, value) {
    try {
      localStorage.setItem(key, value);
      log('localStorage set:', key + '=' + value);
    } catch (e) {
      log('localStorage set failed:', e.message);
    }
  }

  /**
   * Get localStorage value
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  function getLocalStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      log('localStorage get failed:', e.message);
      return null;
    }
  }

  /**
   * Remove localStorage value
   * @param {string} key - Storage key
   */
  function removeLocalStorage(key) {
    try {
      localStorage.removeItem(key);
      log('localStorage removed:', key);
    } catch (e) {
      log('localStorage remove failed:', e.message);
    }
  }

  // =============================================================================
  // REFERRAL CODE FUNCTIONS
  // =============================================================================

  /**
   * Parse referral code from URL
   * @returns {string|null} - Referral code or null
   */
  function parseReferralFromURL() {
    try {
      var urlParams = new URLSearchParams(window.location.search);
      var code = urlParams.get(CONFIG.paramName);

      if (code && isValidCode(code)) {
        log('Parsed from URL:', code.toUpperCase());
        return code.toUpperCase();
      }
    } catch (e) {
      // Fallback for older browsers
      var regex = new RegExp('[?&]' + CONFIG.paramName + '=([^&#]*)');
      var results = regex.exec(window.location.search);
      if (results && results[1]) {
        var code = decodeURIComponent(results[1]);
        if (isValidCode(code)) {
          log('Parsed from URL (fallback):', code.toUpperCase());
          return code.toUpperCase();
        }
      }
    }
    return null;
  }

  /**
   * Store referral code in both localStorage and cookie
   * @param {string} code - Referral code to store
   */
  function storeReferralCode(code) {
    if (!isValidCode(code)) {
      log('Invalid code, not storing:', code);
      return;
    }

    var sanitizedCode = sanitize(code.toUpperCase());
    var timestamp = now().toString();

    // Primary: localStorage
    setLocalStorage(CONFIG.localStorageKey, sanitizedCode);
    setLocalStorage(CONFIG.localStorageTimestampKey, timestamp);

    // Backup: cookie
    setCookie(CONFIG.cookieName, sanitizedCode, CONFIG.expiryDays);

    log('Referral code stored:', sanitizedCode);

    // Dispatch custom event for other scripts
    try {
      window.dispatchEvent(new CustomEvent('pawmate:referral:stored', {
        detail: { code: sanitizedCode, timestamp: parseInt(timestamp) }
      }));
    } catch (e) {
      log('Event dispatch failed:', e.message);
    }
  }

  /**
   * Retrieve referral code from storage
   * @returns {string|null} - Referral code or null
   */
  function getReferralCode() {
    // Try localStorage first
    var code = getLocalStorage(CONFIG.localStorageKey);
    var timestamp = parseInt(getLocalStorage(CONFIG.localStorageTimestampKey) || '0');

    if (code && !isExpired(timestamp)) {
      log('Retrieved from localStorage:', code);
      return code;
    }

    // Fallback to cookie
    var cookieCode = getCookie(CONFIG.cookieName);
    if (cookieCode && isValidCode(cookieCode)) {
      log('Retrieved from cookie:', cookieCode);
      // Sync back to localStorage
      storeReferralCode(cookieCode);
      return cookieCode;
    }

    // Clear expired data
    if (code && isExpired(timestamp)) {
      log('Code expired, clearing');
      clearReferralCode();
    }

    return null;
  }

  /**
   * Clear referral code from all storage
   */
  function clearReferralCode() {
    removeLocalStorage(CONFIG.localStorageKey);
    removeLocalStorage(CONFIG.localStorageTimestampKey);
    deleteCookie(CONFIG.cookieName);
    log('Referral code cleared');

    // Dispatch custom event
    try {
      window.dispatchEvent(new CustomEvent('pawmate:referral:cleared'));
    } catch (e) {
      log('Event dispatch failed:', e.message);
    }
  }

  /**
   * Get time remaining until referral code expires
   * @returns {object|null} - { days, hours, minutes } or null
   */
  function getExpiryInfo() {
    var timestamp = parseInt(getLocalStorage(CONFIG.localStorageTimestampKey) || '0');
    if (!timestamp || isExpired(timestamp)) return null;

    var expiryMs = CONFIG.expiryDays * 24 * 60 * 60 * 1000;
    var remaining = (timestamp + expiryMs) - now();

    if (remaining <= 0) return null;

    return {
      days: Math.floor(remaining / (24 * 60 * 60 * 1000)),
      hours: Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)),
      minutes: Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000))
    };
  }

  // =============================================================================
  // SHOPIFY INTEGRATION
  // =============================================================================

  /**
   * Apply referral code to Shopify cart attributes
   * @returns {Promise} - Promise resolving when cart is updated
   */
  function applyToCart() {
    var code = getReferralCode();
    if (!code) {
      log('No referral code to apply to cart');
      return Promise.resolve(null);
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          [CONFIG.cartAttributeName]: code
        }
      })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Cart update failed');
      return response.json();
    })
    .then(function(cart) {
      log('Referral code applied to cart:', code);
      return cart;
    })
    .catch(function(error) {
      log('Failed to apply to cart:', error.message);
      return null;
    });
  }

  /**
   * Check if current page is checkout
   * @returns {boolean}
   */
  function isCheckoutPage() {
    return window.location.pathname.indexOf('/checkout') === 0 ||
           window.location.pathname.indexOf('/cart') === 0;
  }

  /**
   * Check if current page is thank you page (order complete)
   * @returns {boolean}
   */
  function isThankYouPage() {
    return window.location.pathname.indexOf('/thank_you') !== -1 ||
           window.location.pathname.indexOf('/orders/') !== -1 ||
           (typeof Shopify !== 'undefined' && Shopify.Checkout &&
            Shopify.Checkout.step === 'thank_you');
  }

  // =============================================================================
  // ANALYTICS TRACKING
  // =============================================================================

  /**
   * Track referral event for analytics
   * @param {string} eventName - Event name
   * @param {object} data - Event data
   */
  function trackEvent(eventName, data) {
    // Check analytics consent before tracking
    if (!window.pawmateConsent || !window.pawmateConsent.hasAnalytics()) {
      log('Analytics consent not given, skipping:', eventName);
      return;
    }

    // Google Analytics 4
    if (typeof gtag === 'function') {
      gtag('event', eventName, data);
      log('GA4 event tracked:', eventName);
    }

    // Shopify Analytics
    if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.lib) {
      try {
        ShopifyAnalytics.lib.track('Pawmate ' + eventName, data);
        log('Shopify Analytics event tracked:', eventName);
      } catch (e) {
        // Silent fail
      }
    }
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  /**
   * Initialize referral tracking system
   */
  function init() {
    log('Initializing referral tracking system');

    // Step 1: Parse referral code from URL
    var urlCode = parseReferralFromURL();

    if (urlCode) {
      // New referral code from URL - store it
      storeReferralCode(urlCode);

      // Track referral visit
      trackEvent('referral_visit', {
        referral_code: urlCode,
        landing_page: window.location.pathname
      });

      // Clean URL (remove ?ref= parameter) - optional
      if (window.history && window.history.replaceState) {
        var cleanURL = window.location.href.replace(/[?&]ref=[^&#]*/g, '');
        cleanURL = cleanURL.replace(/\?$/, '').replace(/\?&/, '?');
        window.history.replaceState({}, document.title, cleanURL);
        log('URL cleaned');
      }
    }

    // Step 2: Check for existing referral code
    var existingCode = getReferralCode();
    if (existingCode) {
      log('Active referral code:', existingCode);

      // Apply to cart if on checkout-related page
      if (isCheckoutPage()) {
        applyToCart();
      }
    }

    // Step 3: Clear referral code on order completion
    if (isThankYouPage()) {
      var completedCode = getReferralCode();
      if (completedCode) {
        trackEvent('referral_conversion', {
          referral_code: completedCode
        });
        clearReferralCode();
        log('Referral cleared after order completion');
      }
    }

    // Step 4: Listen for cart additions to apply referral
    document.addEventListener('cart:updated', function() {
      if (getReferralCode()) {
        applyToCart();
      }
    });

    // Also listen for custom Pawmate cart events
    window.addEventListener('pawmate:cart:updated', function() {
      if (getReferralCode()) {
        applyToCart();
      }
    });

    log('Referral tracking initialized');
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Expose public API for external use
   */
  window.PawmateReferral = {
    // Get current referral code
    getCode: getReferralCode,

    // Clear referral code manually
    clear: clearReferralCode,

    // Apply referral to cart
    applyToCart: applyToCart,

    // Get expiry information
    getExpiryInfo: getExpiryInfo,

    // Validate code format
    isValidCode: isValidCode,

    // Enable debug mode
    enableDebug: function() {
      CONFIG.debug = true;
      console.log('[Pawmate Referral] Debug mode enabled');
    },

    // Get stored referral info (for display purposes)
    getInfo: function() {
      var code = getReferralCode();
      if (!code) return null;
      return {
        code: code,
        expiry: getExpiryInfo()
      };
    }
  };

  // =============================================================================
  // RUN INITIALIZATION
  // =============================================================================

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* cdn-refresh 2026-07-27 eu-launch */
